import {
  Component, Input, Output,
  EventEmitter, OnInit, OnDestroy,
} from "@angular/core";
import {
  PurchaseOrderModel, PurchaseOrderCategory, ItsmPurchaseOrderModel,
  purchaseOrderModelFactory, purchaseOrderModelListFactory,
  arePurchaseOrdersEqual,
} from "../../purchase-order/purchase-order.model";
import {PurchaseOrdersSelectDialogComponent, SelectDialogPurchaseOrderModel} from "./purchase-orders-select-dialog.component";
import {DialogService} from "../../bootstrap-modal/dialog.service";
import * as uuid from "uuid";
import {PurchaseOrdersDartOffersSelectDialogComponent} from "../../purchase-order/purchase-orders-dart-offers-select-dialog.component";
import {ConfirmDialogService} from "../../confirm-dialog/confirm-dialog-service";
import { PurchaseOrderService } from "../../purchase-order/purchase-order.service";
import { Subscription } from "rxjs";

@Component({
  selector: "purchase-order-grid",
  templateUrl: "./purchase-order-edit-grid.template.pug",
  providers: [ConfirmDialogService],
})
export class PurchaseOrderEditGridComponent implements OnInit, OnDestroy {
  @Input()
  set purchaseOrder(value: any) {
    this._purchaseOrder = purchaseOrderModelFactory(value);
  }

  newPoSubscription: Subscription;

  @Input()
  set purchaseOrderCategory(value: PurchaseOrderCategory) {
    this._purchaseOrderCategory = value;
    this.setPurchaseOrderCategoryState();
  }

  public mainPage: string = "mainDetails";

  @Input() public purchaseOrdersITSM: ItsmPurchaseOrderModel[] = [];
  @Input() public enableDartOffers: boolean = false;
  @Input() public showSelectPOLink: boolean = true;
  @Input() public selectedPurchaseOrders: PurchaseOrderModel[];
  @Input() public projectId: string;
  private _purchaseOrdersType: string = "NONE";
  public error: Error;
  public newPurchaseOrders: PurchaseOrderModel[];
  public itemNumber = "N/A";
  public _purchaseOrder: PurchaseOrderModel = purchaseOrderModelFactory({});
  public _purchaseOrderIndex: number = 0;
  public _purchaseOrderCategory: PurchaseOrderCategory = PurchaseOrderCategory.customerPO;
  @Input() public firstTimeVisit: boolean = false;
  @Input() public readOnlyInputs: boolean;
  @Output() public newPoSelectedEvent: EventEmitter<PurchaseOrderModel> = new EventEmitter();
  @Output() public removedPOEvent: EventEmitter<PurchaseOrderModel> = new EventEmitter();
  @Input() isDisabled: boolean;

  constructor(public dialogService: DialogService,
    private confirmDialogService: ConfirmDialogService,
    private purchaseOrderService: PurchaseOrderService) {}

  ngOnInit(): void {
    this.newPoSubscription = this.purchaseOrderService.monthlyPOChange.subscribe((value) => {
      this.isDisabled = value;
      if (value) {
        this._purchaseOrder.pendingPO = false;
        this._purchaseOrder.perSignedSOW = false;
        this._purchaseOrder.monthlyPo = true;
        this._purchaseOrder.purchaseOrderNumber = "MOPO";
        this._purchaseOrder.purchaseOrderDescription = "Monthly PO";
        this.newPoSelectedEvent.emit(this._purchaseOrder);
      } else {
        this._purchaseOrder.monthlyPo = false;
        this._purchaseOrder.purchaseOrderNumber = "";
        this._purchaseOrder.purchaseOrderDescription = "";
      }
    });
  }

  @Input() set purchaseOrdersType(value: string) {
    this.mainPage = "mainDetails";
    this._purchaseOrdersType = value;
  }

  get purchaseOrdersType() {
    return this._purchaseOrdersType;
  }

  get purchaseOrderIndex(): number { return this._purchaseOrderIndex; }

  @Input()
  set purchaseOrderIndex(value: number) {
    if (value >= 0) {
      this._purchaseOrderIndex = value;
      this.refreshPendingPONumbers();
    }
  }

  private refreshPendingPONumbers() {
    if (this._purchaseOrderCategory === PurchaseOrderCategory.pendingPO && this.firstTimeVisit) {
      this._purchaseOrder.purchaseOrderNumber = `Pending PO ${this._purchaseOrderIndex + 1}`;
    }
  }

  private setPurchaseOrderCategoryState() {
    switch (this._purchaseOrderCategory) {
      case PurchaseOrderCategory.customerPO:
        this._purchaseOrder.pendingPO = false;
        this._purchaseOrder.perSignedSOW = false;
        if (this.firstTimeVisit) {
          this._purchaseOrder.purchaseOrderNumber = "";
          this._purchaseOrder.purchaseOrderDescription = "";
        }
        this.newPoSelectedEvent.emit(this._purchaseOrder);
        break;
      case PurchaseOrderCategory.pendingPO:
        this._purchaseOrder.pendingPO = true;
        this._purchaseOrder.purchaseOrderNumber = `Pending PO ${this._purchaseOrderIndex + 1}`;
        this._purchaseOrder.purchaseOrderDescription = "";
        this.newPoSelectedEvent.emit(this._purchaseOrder);
        break;
      case PurchaseOrderCategory.perSignedSow:
        this._purchaseOrder.perSignedSOW = true;
        this._purchaseOrder.purchaseOrderNumber = "Per Signed SOW";
        this._purchaseOrder.purchaseOrderDescription = "Per Signed SOW";
        this.newPoSelectedEvent.emit(this._purchaseOrder);
        break;
      case PurchaseOrderCategory.monthlyPO:
        this._purchaseOrder.pendingPO = false;
        this._purchaseOrder.perSignedSOW = false;
        this._purchaseOrder.monthlyPo = true;
        this._purchaseOrder.purchaseOrderNumber = "MOPO";
        this._purchaseOrder.purchaseOrderDescription = "Monthly PO";
        this.newPoSelectedEvent.emit(this._purchaseOrder);
        break;
      default:
        break;
    }
  }

  async selectPurchaseOrderDialog() {
    const purchaseOrders: SelectDialogPurchaseOrderModel[] = [];
    const selectedPurchaseOrdersDictionary: any = this.selectedPurchaseOrders.reduce(
      (acc, iterator) => {
        if (iterator.purchaseOrderITSMId) acc[iterator.purchaseOrderITSMId] = true;
        return acc;
      },
      {},
    );
    purchaseOrders.push(...this.purchaseOrdersITSM.map(poItsm => ({
      ...purchaseOrderModelFactory(poItsm),
      selected: arePurchaseOrdersEqual(purchaseOrderModelFactory(poItsm), this._purchaseOrder),
      disabled: !!selectedPurchaseOrdersDictionary[poItsm.purchaseOrderITSMId],
      uniqueId: uuid.v1(),
    })));
    purchaseOrders.push(...this.selectedPurchaseOrders
      .filter(po => (!po.purchaseOrderITSMId && !!po.purchaseOrderNumber &&
        !!po.purchaseOrderDescription))
      .map(po => ({
        ...po,
        selected: arePurchaseOrdersEqual(po, this._purchaseOrder),
        disabled: true,
        uniqueId: uuid.v1(),
      })));
    for (let i = purchaseOrders.length - 1; i >= 0; i--) {
      if (purchaseOrders[i].monthlyPo) {
        purchaseOrders.splice(i, 1);
      }
    }

    await PurchaseOrdersSelectDialogComponent.showSelectPurchaseOrdersDialogModal(
      this.dialogService,
      {purchaseOrders},
    ).subscribe((result) => {
      if (result.selectedPurchaseOrder) {
        this._purchaseOrder.purchaseOrderITSMId = result.selectedPurchaseOrder.purchaseOrderITSMId;
        this._purchaseOrder.purchaseOrderNumber = result.selectedPurchaseOrder.purchaseOrderNumber;
        this._purchaseOrder.purchaseOrderDescription = result.selectedPurchaseOrder.purchaseOrderDescription;
        this._purchaseOrder.perSignedSOW = result.selectedPurchaseOrder.perSignedSOW;
        this._purchaseOrder.pendingPO = result.selectedPurchaseOrder.pendingPO;
        this._purchaseOrder.createdOn = result.selectedPurchaseOrder.createdOn;
        this._purchaseOrder.purchaseOrderId = result.selectedPurchaseOrder.purchaseOrderId;
        this.newPoSelectedEvent.emit(this._purchaseOrder);
      }
      if (result.newPurchaseOrders) {
        this.newPurchaseOrders = purchaseOrderModelListFactory(result.newPurchaseOrders);
      }
    });
  }

  async selectPurchaseOrderOffersDialog() {
    const dartOffersGrids = [];
    this.selectedPurchaseOrders.forEach((po) => {
      if (po.dartOffers.length) po.dartOffers.forEach(dartOffersSelected => dartOffersGrids.push(dartOffersSelected));
    });
    const selectedDartOffers = await this.dialogService.showDialog(
      PurchaseOrdersDartOffersSelectDialogComponent,
      {
        projectId: this.projectId,
        purchaseOrder: this._purchaseOrder || purchaseOrderModelFactory([]),
        preSelectedDartOffers: dartOffersGrids,
      },
    );

    if (selectedDartOffers) {
      this._purchaseOrder.dartOffers = selectedDartOffers;
      this.newPoSelectedEvent.emit(this._purchaseOrder);
    }
  }

  poInputTextValueChanged() {
    this.newPoSelectedEvent.emit(this._purchaseOrder);
  }

  getPurchaseOrder() { // TODO Is this necessary?
    return this._purchaseOrder;
  }

  getNewPurchaseOrders() { // TODO Is this necessary?
    return this.newPurchaseOrders;
  }

  async removePurchaseOrder(purchaseOrderIndex, purchaseOrder) {
    const result = await this.confirmDialogService.confirm(
      "Confirm",
      `Are you sure you want to remove this purchase order? ${purchaseOrder.purchaseOrderNumber}`,
    );
    if (result) this.removedPOEvent.emit(purchaseOrderIndex);
  }

  ngOnDestroy(): void {
    if (this.newPoSubscription) {
      this.newPoSubscription.unsubscribe();
    }
  }
}
